import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFinestraIndietro } from '../utils/finestraIndietro';
import { confermaUscita } from '../utils/uscitaProtetta';
import useAzione from '../navigazione/useAzione';
import {
    Plus, Search, X, FileText, Printer, Check, Trash2, Pencil,
    ChevronLeft, ChevronRight, Building2, User, RotateCcw, Send,
} from 'lucide-react';
import api from '../services/api';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import StatoSdi from '../components/fatturazione/StatoSdi';
import ScontoTotale from '../components/fatturazione/ScontoTotale';
import NonFatturate from '../components/fatturazione/NonFatturate';
import SegnoOpposizioneTs from '../components/SegnoOpposizioneTs';
import AvvisoCfVecchio from '../components/fatturazione/AvvisoCfVecchio';
import { useTogliBozzaVuota } from '../utils/bozzaVuota';
import '../styles/Fatturazione.css';

/**
 * Fatturazione — elenco dei documenti emessi ed emissione di nuovi.
 *
 * Finora le fatture nascevano solo dalla scheda paziente o da una
 * visita: qui la segreteria può emetterne una da zero, che serve per
 * tutto ciò che non passa da una prestazione registrata.
 *
 * L'elenco è paginato lato server: con ventimila documenti l'anno
 * caricarli tutti non è un'opzione.
 */

const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR',
}));

const data = (d) => (d ? new Date(d).toLocaleDateString('it-IT') : '—');

const Fatturazione = () => {
    const navigate = useNavigate();
    const [filtri, setFiltri] = useState({ q: '', stato: '', dal: '', al: '', ts: '' });
    const [elenco, setElenco] = useState(null);
    const [pagina, setPagina] = useState(1);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [nuova, setNuova] = useState(false);
    /* Le azioni della barra comandi (Ctrl K) e di «Nuovo»: lo stesso gesto
       del pulsante della pagina (navigazione/useAzione.js). */
    useAzione({ 'nuovo-documento': () => setNuova(true) });
    const [ripresa, setRipresa] = useState(null);
    const [storno, setStorno] = useState(null);   // { fattura, righe }
    /* IL DOCUMENTO APERTO STA NELL'INDIRIZZO (?documento=…, Alex 28/09/2026):
       «Indietro» lo chiude e torna all'elenco, ricaricare lo riapre. Le
       altre due finestre si chiudono con «Indietro» senza un indirizzo. */
    const [parametri, setParametri] = useSearchParams();
    const documentoAperto = parametri.get('documento');
    const apriDocumento = (id) => setParametri((p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('documento', String(id)); else n.delete('documento');
        return n;
    });
    useFinestraIndietro(nuova, () => { if (!confermaUscita()) return false; setNuova(false); return true; });
    useFinestraIndietro(!!storno, () => { if (!confermaUscita()) return false; setStorno(null); return true; });

    /* L'errore della lettura dell'elenco sta per conto suo, con «Riprova»:
       la lettura riuscita lo toglie, quelli dei gesti restano in `errore`
       (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);
    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const params = { pagina, per_pagina: 50 };
            Object.entries(filtri).forEach(([k, v]) => { if (v) params[k] = v; });
            const r = await conUnTentativo(() => api.get('/fatturazione', { params }));
            setElenco(r.data.data);
            setErrore(null);
            setErroreLettura(null);
        } catch (e) {
            setErroreLettura(motivoDellaLettura(e, 'Non riesco a caricare i documenti'));
        } finally {
            setCaricamento(false);
        }
    }, [filtri, pagina]);

    useEffect(() => { carica(); }, [carica]);

    /* Cambiando un filtro si torna alla prima pagina: restare alla 7
       mentre l'elenco si è ridotto a 2 pagine mostra il vuoto. */
    const cambiaFiltro = (campo, valore) => {
        setPagina(1);
        setFiltri((f) => ({ ...f, [campo]: valore }));
    };

    const scaricaPdf = async (f) => {
        try {
            const r = await api.get(`/fatturazione/${f.id}/pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            setErrore('Non riesco a produrre il PDF');
        }
    };

    const riprendi = (f) => apriDocumento(f.id);
    useEffect(() => {
        if (!documentoAperto) { setRipresa(null); return; }
        let vivo = true;
        api.get(`/fatturazione/${documentoAperto}`)
            .then((r) => { if (vivo) setRipresa(r.data.data); })
            .catch(() => { if (vivo) { setErrore('Non riesco a riaprire la bozza'); apriDocumento(null); } });
        return () => { vivo = false; };
        // eslint-disable-next-line
    }, [documentoAperto]);
    const chiudiRipresa = () => { if (confermaUscita()) apriDocumento(null); };

    /* Lo storno si faceva solo dalla scheda del paziente: chi partiva da
       qui doveva prima cercare il paziente, e su una fattura intestata a
       un'azienda il paziente non c'e' nemmeno. */
    const apriStorno = async (f) => {
        try {
            setErrore(null);
            const r = await api.get(`/fatturazione/${f.id}`);
            setStorno(r.data.data);
        } catch (e) {
            setErrore('Non riesco ad aprire il documento da stornare');
        }
    };

    const eliminaBozza = async (f) => {
        if (!window.confirm('Eliminare questa bozza? Non è recuperabile.')) return;
        try {
            await api.delete(`/fatturazione/${f.id}`);
            carica();
        } catch (e) {
            setErrore(e.response?.data?.message || 'Non riesco a eliminare la bozza');
        }
    };

    const statoDi = (f) => {
        if (f.annullata_il) return { classe: 'annullata', testo: 'Annullata' };
        if (f.stato === 'bozza') return { classe: 'bozza', testo: 'Bozza' };
        const incassato = parseFloat(f.incassato || 0);
        const totale = parseFloat(f.totale || 0);
        if (incassato >= totale && totale > 0) return { classe: 'saldata', testo: 'Saldata' };
        if (incassato > 0) return { classe: 'parziale', testo: 'Acconto' };
        return { classe: 'emessa', testo: 'Da incassare' };
    };

    return (
        <div className="fat-wrap">
            {/* Senza linguette, ed e' voluto: qui non ci sono sotto-viste,
                ci sono filtri - stato, Tessera Sanitaria, periodo - che si
                combinano fra loro. Tre linguette che escludono i filtri
                sarebbero una schermata diversa, non la stessa vestita
                meglio. */}
            <IntestazioneSezione
                titolo="Fatturazione"
                icona={<FileText size={20} aria-hidden="true" />}
                sottotitolo="Documenti emessi ed emissione di nuovi."
                azione={
                    <div className="fat-azioni-testata">
                        {/* L'invio al Sistema TS e' la coda di questo ciclo:
                            si fattura, e poi si manda. Si raggiungeva solo dal
                            riquadro in Amministrazione, che resta dov'e'.
                            Stessi ruoli di questa schermata - admin e
                            segreteria - quindi chi la vede puo' anche aprirlo. */}
                        <button className="fat-btn" onClick={() => navigate('/sistema-ts')}
                                title="Invio delle spese sanitarie al Sistema TS">
                            <Send size={16} /> Sistema TS
                        </button>
                        <button className="fat-btn primario" onClick={() => setNuova(true)}>
                            <Plus size={16} /> Nuovo documento
                        </button>
                    </div>
                }
            />

            <NonFatturate />

            <section className="fat-filtri" data-non-trattiene>
                <div className="fat-cerca">
                    <Search size={15} />
                    <input
                        value={filtri.q}
                        onChange={(e) => cambiaFiltro('q', e.target.value)}
                        placeholder="Nome, codice fiscale o numero…"
                    />
                </div>
                <select value={filtri.stato} onChange={(e) => cambiaFiltro('stato', e.target.value)}>
                    <option value="">Tutti gli stati</option>
                    <option value="bozza">Bozze</option>
                    <option value="emessa">Emesse</option>
                </select>
                <select value={filtri.ts} onChange={(e) => cambiaFiltro('ts', e.target.value)}>
                    <option value="">Tessera Sanitaria</option>
                    <option value="inviate">Trasmesse</option>
                    <option value="da_inviare">Da trasmettere</option>
                </select>
                <input type="date" value={filtri.dal}
                       onChange={(e) => cambiaFiltro('dal', e.target.value)} />
                <input type="date" value={filtri.al}
                       onChange={(e) => cambiaFiltro('al', e.target.value)} />
            </section>

            <ErroreLettura testo={erroreLettura} onRiprova={carica} classe="fat-errore" Contenitore="div" />
            {errore && <div className="fat-errore">{errore}</div>}

            {elenco && (
                <div className="fat-conteggio">
                    {elenco.totale} document{elenco.totale === 1 ? 'o' : 'i'}
                    {elenco.pagine > 1 && (
                        <span className="fat-pagine">
                            <button disabled={pagina <= 1}
                                    onClick={() => setPagina((p) => p - 1)}>
                                <ChevronLeft size={15} />
                            </button>
                            pagina {elenco.pagina} di {elenco.pagine}
                            <button disabled={pagina >= elenco.pagine}
                                    onClick={() => setPagina((p) => p + 1)}>
                                <ChevronRight size={15} />
                            </button>
                        </span>
                    )}
                </div>
            )}

            {caricamento && !elenco && <p className="fat-vuoto">Caricamento…</p>}

            {elenco && !elenco.fatture.length && (
                <p className="fat-vuoto">Nessun documento con questi filtri.</p>
            )}

            {elenco && elenco.fatture.length > 0 && (
                <div className="rm-scorre">
                <table className="fat-tabella">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Data</th>
                            <th>Numero</th>
                            <th>Intestatario</th>
                            <th className="destra">Importo</th>
                            <th>Stato</th>
                            <th>TS</th>
                            <th>SdI</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {elenco.fatture.map((f) => {
                            const s = statoDi(f);
                            return (
                                <tr key={f.id}>
                                    <td className="fat-tipo">
                                        {f.tipo_documento === 'nota_credito'
                                            ? 'Nota di credito' : 'Fattura'}
                                    </td>
                                    <td>{data(f.data_documento)}</td>
                                    <td><b>{f.numero ? `${f.numero}/${f.anno}` : '—'}</b></td>
                                    <td>
                                        <span className="fat-intestatario">
                                            {f.soggetto_tipo === 'azienda'
                                                ? <Building2 size={13} /> : <User size={13} />}
                                            {f.intestatario}
                                        </span>
                                        {/* Il codice al Sistema TS corretto dopo
                                            l'emissione (Alex, 4/10/2026): il
                                            documento porta ancora quello vecchio. */}
                                        <AvvisoCfVecchio fattura={f} />
                                    </td>
                                    <td className="destra">
                                        {euro(f.totale)}
                                        {parseFloat(f.incassato) > 0
                                            && parseFloat(f.incassato) < parseFloat(f.totale) && (
                                            <em className="fat-acconto">
                                                incassati {euro(f.incassato)}
                                            </em>
                                        )}
                                    </td>
                                    <td><span className={`fat-stato ${s.classe}`}>{s.testo}</span></td>
                                    <td>
                                        {f.invio_ts
                                            ? <Check size={15} className="fat-ts-ok" />
                                            : <span className="fat-ts-no">—</span>}
                                    </td>
                                    {/* La fattura elettronica (blocco 6): lo stato,
                                        e l'XML da scaricare o da rigenerare. Il
                                        motivo di un «da correggere» sta nel
                                        suggerimento sullo stato. */}
                                    <td><StatoSdi fattura={f} compatto onCambiata={carica} /></td>
                                    <td className="fat-azioni">
                                        {f.stato === 'bozza' ? (
                                            <>
                                                <button title="Riprendi"
                                                        onClick={() => riprendi(f)}>
                                                    <Pencil size={15} />
                                                </button>
                                                <button title="Elimina la bozza"
                                                        className="pericolo"
                                                        onClick={() => eliminaBozza(f)}>
                                                    <Trash2 size={15} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button title="Stampa"
                                                        onClick={() => scaricaPdf(f)}>
                                                    <Printer size={15} />
                                                </button>
                                                {f.tipo_documento === 'fattura'
                                                    && f.stato === 'emessa'
                                                    && !f.annullata_il && (
                                                    <button title="Storna con nota di credito"
                                                            onClick={() => apriStorno(f)}>
                                                        <RotateCcw size={15} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            )}

            {nuova && (
                <NuovoDocumento
                    onChiudi={() => setNuova(false)}
                    onFatto={() => { setNuova(false); carica(); }}
                    onErrore={setErrore}
                    onTolta={carica}
                />
            )}

            {storno && (
                <ModaleStorno
                    documento={storno}
                    onChiudi={() => setStorno(null)}
                    onFatto={() => { setStorno(null); carica(); }}
                />
            )}

            {ripresa && (
                <div className="fat-modale-fondo" onClick={chiudiRipresa}>
                    <div className="fat-modale grande" onClick={(e) => e.stopPropagation()}>
                        <header>
                            <h3>
                                Bozza per {ripresa.denominazione
                                    || `${ripresa.cognome || ''} ${ripresa.nome || ''}`.trim()
                                    || 'documento'}
                            </h3>
                            <button onClick={chiudiRipresa}><X size={18} /></button>
                        </header>
                        <RigheDocumento
                            bozza={ripresa}
                            onAggiorna={setRipresa}
                            onFatto={() => { apriDocumento(null); carica(); }}
                            onErrore={setErrore}
                            onTolta={carica}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

/* ==========================================================
   Storno con nota di credito

   La nota di credito non e' un annullamento: l'annullamento brucia il
   numero ed e' solo dell'admin. Questa e' la strada normale per
   correggere una fattura emessa sbagliata, e la fa la segreteria.
   ========================================================== */
export const ModaleStorno = ({ documento, onChiudi, onFatto }) => {
    const { fattura, righe } = documento;
    const stornabili = (righe || []).filter((r) => !r.stornata_da_id);

    /* Di norma si storna tutto: chi vuole il parziale toglie le spunte. */
    const [scelte, setScelte] = useState(stornabili.map((r) => r.id));
    const [motivo, setMotivo] = useState('');
    const [inCorso, setInCorso] = useState(false);
    /* Il rifiuto resta nella finestra, con le righe scelte (2/10/2026): se
       la fattura sta partendo per il Sistema TS la nota si rifiuta, e si
       riprova fra un momento senza rifare le scelte. Prima la finestra si
       chiudeva su qualunque errore. */
    const [erroreNota, setErroreNota] = useState(null);

    const crea = async () => {
        try {
            setInCorso(true);
            setErroreNota(null);
            const r = await api.post(`/fatturazione/${fattura.id}/nota-credito`, {
                // Tutte le righe: si omette il parametro, che in database
                // significa "tutte quelle non ancora stornate".
                righe_ids: scelte.length === stornabili.length ? undefined : scelte,
                motivo: motivo || undefined,
            });
            onFatto(r.data?.data?.nota_credito?.id);
        } catch (e) {
            setErroreNota(e.response?.data?.message
                || 'Errore nella creazione della nota di credito');
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="fat-modale-fondo" onClick={onChiudi}>
            <div className="fat-modale" onClick={(e) => e.stopPropagation()}>
                <header>
                    <h3>
                        <RotateCcw size={16} /> Nota di credito su
                        {' '}{fattura.numero}/{fattura.anno}
                    </h3>
                    <button onClick={onChiudi}><X size={18} /></button>
                </header>

                {!stornabili.length ? (
                    <p className="fat-nota">
                        Tutte le righe di questo documento sono già state stornate.
                    </p>
                ) : (
                    <>
                        <p className="fat-nota">
                            Scegli cosa stornare. Le prestazioni stornate tornano
                            disponibili per una nuova fattura e la spettanza al medico
                            viene annullata.
                        </p>
                        <div className="fat-storno-righe">
                            {stornabili.map((r) => (
                                <label key={r.id} className="fat-storno-riga">
                                    <input
                                        type="checkbox"
                                        checked={scelte.includes(r.id)}
                                        onChange={(e) => setScelte(e.target.checked
                                            ? [...scelte, r.id]
                                            : scelte.filter((x) => x !== r.id))}
                                    />
                                    <span>{r.descrizione}</span>
                                    <b>{euro(r.totale)}</b>
                                </label>
                            ))}
                        </div>
                        <input
                            className="fat-storno-motivo"
                            placeholder="Motivo (facoltativo)"
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                        />
                        {erroreNota && <div className="fat-errore" role="alert">{erroreNota}</div>}
                        <div className="fat-modale-azioni">
                            <button className="fat-btn" onClick={onChiudi}>Annulla</button>
                            <button
                                className="fat-btn primario"
                                onClick={crea}
                                disabled={inCorso || !scelte.length}
                            >
                                {inCorso ? 'Creo…' : 'Crea nota di credito'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

/* ==========================================================
   Emissione di un documento nuovo
   ========================================================== */

export const NuovoDocumento = ({ onChiudi, onFatto, onErrore, onTolta }) => {
    const [passo, setPasso] = useState('soggetto');   // soggetto | righe
    const [tipoRicerca, setTipoRicerca] = useState('paziente');
    const [cerca, setCerca] = useState('');
    const [trovati, setTrovati] = useState([]);
    const [soggetto, setSoggetto] = useState(null);
    const [bozza, setBozza] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    /* --- ricerca ---
       I pazienti si cercano in anagrafica, non fra i soggetti di
       fatturazione: quelli nascono alla prima fattura, quindi chi non
       ha mai fatturato non ci sarebbe. Le aziende invece stanno
       proprio nei soggetti. */
    useEffect(() => {
        if (cerca.trim().length < 2) { setTrovati([]); return undefined; }
        let attivo = true;
        const t = setTimeout(async () => {
            try {
                if (tipoRicerca === 'paziente') {
                    const r = await api.get('/pazienti', {
                        params: { search: cerca, limit: 15 },
                    });
                    const lista = r.data.data?.pazienti || [];
                    if (attivo) {
                        setTrovati(lista.map((p) => ({
                            ...p,
                            _paziente: true,
                            denominazione: null,
                        })));
                    }
                } else {
                    const r = await api.get('/fatturazione/soggetti', {
                        params: { q: cerca, tipo: 'azienda' },
                    });
                    const dati = r.data.data;
                    const lista = Array.isArray(dati) ? dati : (dati?.soggetti || []);
                    if (attivo) setTrovati(lista);
                }
            } catch (e) { if (attivo) setTrovati([]); }
        }, 300);
        return () => { attivo = false; clearTimeout(t); };
    }, [cerca, tipoRicerca]);

    const scegli = async (s) => {
        try {
            setInCorso(true);

            /* Per un paziente il soggetto di fatturazione puo' non
               esistere ancora: si crea ricopiando l'anagrafica. Se
               esiste gia', l'endpoint restituisce quello. */
            let soggettoId = s.id;
            if (s._paziente) {
                const c = await api.post(`/fatturazione/soggetti/da-paziente/${s.id}`);
                const creato = c.data.data?.soggetto || c.data.data;
                soggettoId = creato.id;
            }

            const r = await api.post('/fatturazione', { soggetto_id: soggettoId });
            setSoggetto(s);
            setBozza(r.data.data);
            setPasso('righe');
        } catch (e) {
            onErrore(e.response?.data?.message || 'Non riesco ad aprire la bozza');
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="fat-modale-fondo" onClick={onChiudi}>
            <div className="fat-modale grande" onClick={(e) => e.stopPropagation()}>
                <header>
                    <h3>
                        {passo === 'soggetto' ? 'A chi si intesta il documento?'
                            : `Documento per ${soggetto?.denominazione
                                || `${soggetto?.cognome || ''} ${soggetto?.nome || ''}`}`}
                    </h3>
                    <button onClick={onChiudi}><X size={18} /></button>
                </header>

                {passo === 'soggetto' && (
                    <>
                        <div className="fat-tipo-scelta">
                            <button
                                className={tipoRicerca === 'paziente' ? 'attivo' : ''}
                                onClick={() => { setTipoRicerca('paziente'); setTrovati([]); }}
                            >
                                <User size={15} /> Pazienti
                            </button>
                            <button
                                className={tipoRicerca === 'azienda' ? 'attivo' : ''}
                                onClick={() => { setTipoRicerca('azienda'); setTrovati([]); }}
                            >
                                <Building2 size={15} /> Aziende
                            </button>
                        </div>

                        <div className="fat-cerca grande">
                            <Search size={15} />
                            <input
                                autoFocus
                                value={cerca}
                                onChange={(e) => setCerca(e.target.value)}
                                placeholder={tipoRicerca === 'paziente'
                                    ? 'Cognome, nome o codice fiscale…'
                                    : 'Ragione sociale o partita IVA…'}
                            />
                        </div>

                        {cerca.trim().length >= 2 && !trovati.length && (
                            <p className="fat-vuoto">Nessun risultato.</p>
                        )}

                        <ul className="fat-risultati">
                            {trovati.map((s) => (
                                <li key={s.id}>
                                    <button disabled={inCorso} onClick={() => scegli(s)}>
                                        <b>
                                            {s.denominazione || `${s.cognome || ''} ${s.nome || ''}`}
                                        </b>
                                        <em>
                                            {s.codice_fiscale || s.partita_iva || ''}
                                            {s.citta ? ` · ${s.citta}` : ''}
                                        </em>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}

                {passo === 'righe' && bozza && (
                    <RigheDocumento
                        bozza={bozza}
                        onAggiorna={setBozza}
                        onFatto={onFatto}
                        onErrore={onErrore}
                        onTolta={onTolta}
                    />
                )}
            </div>
        </div>
    );
};

/* ==========================================================
   Righe, totali, emissione e incasso
   ========================================================== */

/**
 * VisiteLavoro — le visite di medicina del lavoro da fatturare a
 * un'azienda (proposta 18, migration 266): quelle dei suoi lavoratori, nel
 * periodo scelto (di partenza il mese in corso), non ancora fatturate. Si
 * spuntano e si aggiungono al documento: una riga per visita, e la
 * descrizione la compone il server (voce, lavoratore, data - mai l'esito).
 */
const VisiteLavoro = ({ fatturaId, soggettoId, onAggiunte, onErrore }) => {
    const oggi = new Date();
    const due = (n) => String(n).padStart(2, '0');
    const [dal, setDal] = useState(`${oggi.getFullYear()}-${due(oggi.getMonth() + 1)}-01`);
    const [al, setAl] = useState(() => {
        const fine = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 0);
        return `${fine.getFullYear()}-${due(fine.getMonth() + 1)}-${due(fine.getDate())}`;
    });
    const [visite, setVisite] = useState(null);
    const [scelte, setScelte] = useState({});
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            const r = await api.get(`/fatturazione/aziende/${soggettoId}/visite-lavoro`, { params: { dal, al } });
            const v = r.data.data?.visite || [];
            setVisite(v);
            setScelte(Object.fromEntries(v.map((x) => [x.id, true])));
        } catch (e) {
            setVisite([]);
            onErrore(e.response?.data?.message || 'Non riesco a leggere le visite da fatturare');
        }
    }, [soggettoId, dal, al, onErrore]);
    useEffect(() => { carica(); }, [carica]);

    const aggiungi = async () => {
        setInCorso(true);
        try {
            for (const v of (visite || []).filter((x) => scelte[x.id])) {
                await api.post(`/fatturazione/${fatturaId}/righe`, { visita_prestazione_id: v.id });
            }
            await onAggiunte();
            await carica();
        } catch (e) {
            onErrore(e.response?.data?.message || 'Non riesco ad aggiungere le visite');
            await onAggiunte();
            await carica();
        } finally {
            setInCorso(false);
        }
    };

    if (visite === null) return null;
    const quante = (visite || []).filter((x) => scelte[x.id]).length;
    return (
        <section className="fat-visite-lavoro">
            <h4>Visite di medicina del lavoro da fatturare</h4>
            <div className="fat-visite-lavoro-periodo">
                <label>dal <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} /></label>
                <label>al <input type="date" value={al} onChange={(e) => setAl(e.target.value)} /></label>
            </div>
            {visite.length === 0 ? (
                <p className="fat-vuoto">Nessuna visita da fatturare per questa azienda nel periodo.</p>
            ) : (
                <>
                    <ul>
                        {visite.map((v) => (
                            <li key={v.id}>
                                <label>
                                    <input type="checkbox" checked={!!scelte[v.id]}
                                           onChange={(e) => setScelte((x) => ({ ...x, [v.id]: e.target.checked }))} />
                                    {data(v.data_visita)} — {v.cognome} {v.nome} — {v.nome_snapshot}
                                </label>
                            </li>
                        ))}
                    </ul>
                    <button className="fat-btn" disabled={inCorso || !quante} onClick={aggiungi}>
                        <Plus size={15} /> Aggiungi al documento ({quante})
                    </button>
                </>
            )}
        </section>
    );
};

export const RigheDocumento = ({ bozza, onAggiorna, onFatto, onErrore, onTolta }) => {
    const id = bozza.fattura?.id || bozza.id;
    /* D5 (Alex, 4/10/2026): chiudendo «Nuovo documento» o la bozza ripresa
       - con la X, col fondo, con «Indietro», cambiando pagina - una bozza
       rimasta senza righe si toglie da sola. Decide il server: con una riga
       resta, e non succede niente. */
    useTogliBozzaVuota(bozza, onTolta);
    const [catalogo, setCatalogo] = useState([]);
    const [cerca, setCerca] = useState('');
    const [libera, setLibera] = useState({ descrizione: '', prezzo_unitario: '', quantita: 1 });
    const [metodi, setMetodi] = useState([]);
    const [medici, setMedici] = useState([]);
    const [medicoId, setMedicoId] = useState('');
    const [emessa, setEmessa] = useState(null);
    const [incasso, setIncasso] = useState({ importo: '', metodo_pagamento: '' });
    const [inCorso, setInCorso] = useState(false);

    const ricarica = useCallback(async () => {
        try {
            const r = await api.get(`/fatturazione/${id}`);
            onAggiorna(r.data.data);
        } catch (e) { /* si resta com'era */ }
    }, [id, onAggiorna]);

    useEffect(() => {
        api.get('/fatturazione/metodi-pagamento')
            .then((r) => {
                const d = r.data.data;
                setMetodi(Array.isArray(d) ? d : (d?.metodi || []));
            })
            .catch(() => setMetodi([]));

        api.get('/medici')
            .then((r) => {
                const d = r.data.data;
                setMedici(Array.isArray(d) ? d : (d?.medici || []));
            })
            .catch(() => setMedici([]));
    }, []);

    useEffect(() => {
        if (cerca.trim().length < 3) { setCatalogo([]); return undefined; }
        let attivo = true;
        const t = setTimeout(async () => {
            try {
                const r = await api.get('/prestazioni', {
                    params: { soloAttive: 'true' },
                });
                const tutte = r.data.data?.prestazioni || [];
                const q = cerca.trim().toLowerCase();
                if (attivo) {
                    setCatalogo(tutte.filter((p) => (
                        (p.nome || '').toLowerCase().includes(q)
                        || (p.codice || '').toLowerCase().includes(q)
                    )).slice(0, 15));
                }
            } catch (e) { if (attivo) setCatalogo([]); }
        }, 300);
        return () => { attivo = false; clearTimeout(t); };
    }, [cerca]);

    const aggiungiDaCatalogo = async (p) => {
        /* Il medico serve solo per i servizi: la spettanza matura su
           chi esegue, e un prodotto venduto al banco non ha un
           esecutore. */
        const servizio = p.tipologia !== 'bene';
        if (servizio && !medicoId) {
            onErrore('Scegli prima il medico che ha eseguito la prestazione.');
            return;
        }
        try {
            const corpo = { prestazione_id: p.id };
            if (servizio) corpo.medico_id = parseInt(medicoId, 10);
            await api.post(`/fatturazione/${id}/righe`, corpo);
            setCerca('');
            setCatalogo([]);
            ricarica();
        } catch (e) {
            onErrore(e.response?.data?.message || 'Non riesco ad aggiungere la riga');
        }
    };

    const aggiungiLibera = async () => {
        if (!libera.descrizione.trim() || !libera.prezzo_unitario) return;
        try {
            await api.post(`/fatturazione/${id}/righe`, {
                descrizione: libera.descrizione.trim(),
                prezzo_unitario: parseFloat(libera.prezzo_unitario),
                quantita: parseFloat(libera.quantita) || 1,
            });
            setLibera({ descrizione: '', prezzo_unitario: '', quantita: 1 });
            ricarica();
        } catch (e) {
            onErrore(e.response?.data?.message || 'Non riesco ad aggiungere la riga');
        }
    };

    const togliRiga = async (rigaId) => {
        try {
            await api.delete(`/fatturazione/${id}/righe/${rigaId}`);
            ricarica();
        } catch (e) {
            /* Il motivo del server: per esempio la riga di una nota di credito
               la cui fattura sta partendo per il Sistema TS. */
            onErrore(e.response?.data?.message || 'Non riesco a togliere la riga');
        }
    };

    const emetti = async () => {
        if (!window.confirm('Emettere il documento? Dopo non si potranno più cambiare le righe.')) return;
        try {
            setInCorso(true);
            const r = await api.post(`/fatturazione/${id}/emetti`);
            setEmessa(r.data.data?.fattura || r.data.data);
            ricarica();
        } catch (e) {
            onErrore(e.response?.data?.message || 'Non riesco a emettere');
        } finally {
            setInCorso(false);
        }
    };

    const registraIncasso = async () => {
        if (!incasso.metodo_pagamento) { onErrore('Indica come è stato pagato'); return; }
        try {
            setInCorso(true);
            await api.post(`/fatturazione/${id}/incassi`, {
                importo: parseFloat(incasso.importo) || totale,
                metodo_pagamento: incasso.metodo_pagamento,
            });
            onFatto();
        } catch (e) {
            onErrore(e.response?.data?.message || 'Non riesco a registrare l\'incasso');
        } finally {
            setInCorso(false);
        }
    };

    const f = bozza.fattura || bozza;
    const righe = bozza.righe || [];
    const totale = parseFloat(f.totale || 0);

    return (
        <div className="fat-documento">
            {/* L'opposizione al Sistema TS del paziente intestatario, come in
                cassa (F2, Alex 4/10/2026): anche da qui si emette. */}
            {f.opposizione_ts_attiva === true && (
                <div className="fat-segni">
                    <SegnoOpposizioneTs attiva dal={f.opposizione_ts_dal} />
                </div>
            )}
            {!emessa && (
                <>
                    <div className="fat-aggiungi">
                        <label className="fat-medico">
                            <span>Eseguita da</span>
                            <select
                                value={medicoId}
                                onChange={(e) => setMedicoId(e.target.value)}
                            >
                                <option value="">Scegli il medico…</option>
                                {medici.map((m) => (
                                    <option key={m.id} value={m.id}>
                                        {m.cognome} {m.nome}
                                        {m.specializzazione ? ` — ${m.specializzazione}` : ''}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="fat-cerca">
                            <Search size={15} />
                            <input
                                value={cerca}
                                onChange={(e) => setCerca(e.target.value)}
                                placeholder="Cerca a catalogo: almeno tre lettere…"
                            />
                        </div>
                        {catalogo.length > 0 && (
                            <ul className="fat-risultati compatti">
                                {catalogo.map((p) => (
                                    <li key={p.id}>
                                        <button onClick={() => aggiungiDaCatalogo(p)}>
                                            <b>{p.nome}</b>
                                            <em>
                                                {p.codice}
                                                {p.prezzo_indicativo
                                                    ? ` · ${euro(p.prezzo_indicativo)}` : ''}
                                            </em>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <details className="fat-libera">
                        <summary>Aggiungi una voce non a catalogo</summary>
                        <div className="fat-libera-campi">
                            <input
                                placeholder="Descrizione"
                                value={libera.descrizione}
                                onChange={(e) => setLibera({ ...libera, descrizione: e.target.value })}
                            />
                            <input
                                type="number" step="0.01" placeholder="Prezzo"
                                value={libera.prezzo_unitario}
                                onChange={(e) => setLibera({ ...libera, prezzo_unitario: e.target.value })}
                            />
                            <input
                                type="number" step="1" min="1" placeholder="Q.tà"
                                value={libera.quantita}
                                onChange={(e) => setLibera({ ...libera, quantita: e.target.value })}
                            />
                            <button className="fat-btn" onClick={aggiungiLibera}>Aggiungi</button>
                        </div>
                    </details>
                </>
            )}

            {!righe.length ? (
                <p className="fat-vuoto">Nessuna riga. Aggiungine almeno una per emettere.</p>
            ) : (
                <table className="fat-righe">
                    <tbody>
                        {righe.map((r) => (
                            <tr key={r.id}>
                                <td>
                                    {r.descrizione}
                                    {/* D12: la rata con un'aliquota diversa dalle altre
                                        rate del pacchetto (la frase e' del server). */}
                                    {r.avviso_aliquota && (
                                        <span className="fat-avviso-riga">{r.avviso_aliquota}</span>
                                    )}
                                </td>
                                <td className="destra">{r.quantita > 1 ? `×${r.quantita}` : ''}</td>
                                <td className="destra">{euro(r.totale)}</td>
                                {!emessa && (
                                    <td>
                                        <button className="fat-icona" onClick={() => togliRiga(r.id)}>
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Solo in una FATTURA: una nota di credito storna le righe della
                fattura da cui nasce, e il server rifiuta che le si aggiunga
                una prestazione (revisione del 3/10/2026: riaperta con
                «Riprendi», la nota di un'azienda mostrava il riquadro). */}
            {!emessa && f.stato === 'bozza' && f.tipo === 'azienda' && f.tipo_documento === 'fattura' && (
                <VisiteLavoro fatturaId={id} soggettoId={f.soggetto_id} onAggiunte={ricarica} onErrore={onErrore} />
            )}

            {/* Lo sconto sul totale (Alex, 28/09/2026): lo stesso componente della
                Cassa, e la stessa ripartizione, quella del database (262). */}
            {!emessa && f.stato === 'bozza' && (
                <ScontoTotale fatturaId={id} sconto={f.sconto_finale} onAggiornato={ricarica} onErrore={onErrore} />
            )}

            <div className="fat-totali">
                {parseFloat(f.sconto_finale || 0) > 0 && (
                    <div><span>Sconto</span><b>− {euro(f.sconto_finale)}</b></div>
                )}
                <div><span>Imponibile</span><b>{euro(f.totale_imponibile)}</b></div>
                <div><span>IVA</span><b>{euro(f.totale_iva)}</b></div>
                {parseFloat(f.bollo) > 0 && (
                    <div><span>Bollo</span><b>{euro(f.bollo)}</b></div>
                )}
                <div className="grande"><span>Totale</span><b>{euro(f.totale)}</b></div>
            </div>

            {!emessa ? (
                <footer className="fat-modale-azioni">
                    <button
                        className="fat-btn primario"
                        disabled={!righe.length || inCorso}
                        onClick={emetti}
                    >
                        <FileText size={15} /> Emetti
                    </button>
                </footer>
            ) : (
                <div className="fat-incasso">
                    <p className="fat-emessa">
                        <Check size={16} /> Documento emesso
                        {emessa.numero ? ` con il numero ${emessa.numero}/${emessa.anno}` : ''}.
                    </p>
                    <h4>È stato pagato?</h4>
                    <div className="fat-incasso-campi">
                        <select
                            value={incasso.metodo_pagamento}
                            onChange={(e) => setIncasso({ ...incasso, metodo_pagamento: e.target.value })}
                        >
                            <option value="">Come è stato pagato…</option>
                            {metodi.map((m) => (
                                <option key={m.codice} value={m.codice}>{m.descrizione}</option>
                            ))}
                        </select>
                        <input
                            type="number" step="0.01"
                            placeholder={String(totale.toFixed(2))}
                            value={incasso.importo}
                            onChange={(e) => setIncasso({ ...incasso, importo: e.target.value })}
                        />
                        <button className="fat-btn primario" disabled={inCorso}
                                onClick={registraIncasso}>
                            Registra incasso
                        </button>
                    </div>
                    <p className="fat-nota">
                        Lasciando vuoto l&apos;importo si registra il saldo completo.
                        Si può anche chiudere e incassare più tardi.
                    </p>
                    <button className="fat-btn" onClick={onFatto}>Chiudi</button>
                </div>
            )}
        </div>
    );
};

export default Fatturazione;
